import { useEffect, useMemo, useState } from "react";
import { api } from "@/lib/api";
import { PageHeader } from "@/components/app/UI";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger } from "@/components/ui/dialog";
import { Plus, Trash2 } from "lucide-react";
import { toast } from "sonner";

const CONDITIONS = ["Good", "Damaged", "Rejected"];

export default function GRN() {
  const [grns, setGrns] = useState([]);
  const [pos, setPos] = useState([]);
  const [products, setProducts] = useState([]);
  const [vendors, setVendors] = useState([]);
  const [open, setOpen] = useState(false);

  const load = async () => {
    const [g, p, pr, v] = await Promise.all([
      api.get("/purchasing/grn"),
      api.get("/purchasing/purchase-orders"),
      api.get("/purchasing/products"),
      api.get("/purchasing/vendors"),
    ]);
    setGrns(g.data); setPos(p.data); setProducts(pr.data); setVendors(v.data);
  };
  useEffect(() => { load(); }, []);

  const productName = useMemo(() => Object.fromEntries(products.map((p) => [p.id, p.name])), [products]);
  const vendorName = useMemo(() => Object.fromEntries(vendors.map((v) => [v.id, v.name])), [vendors]);

  const del = async (g) => {
    if (!window.confirm(`Reverse & delete ${g.grn_number}? PO quantities will be rolled back.`)) return;
    try { await api.delete(`/purchasing/grn/${g.id}`); toast.success("GRN reversed"); load(); }
    catch (e) { toast.error(e.response?.data?.detail || "Delete failed"); }
  };

  // Only Sent/Accepted/Partially Delivered POs are receivable.
  const receivablePos = pos.filter((p) => ["Sent", "Accepted", "Partially Delivered", "Draft"].includes(p.status));

  return (
    <div data-testid="pv-grn-page">
      <PageHeader
        eyebrow="Purchasing"
        title="Goods Receipt (GRN)"
        subtitle="Record deliveries against POs. Partial receipts supported — PO status auto-updates."
        action={
          <Dialog open={open} onOpenChange={setOpen}>
            <DialogTrigger asChild>
              <Button className="rounded-none bg-[#002FA7] hover:bg-[#002480] uppercase tracking-widest text-xs font-bold h-11 px-6" data-testid="pv-new-grn-btn">
                <Plus className="w-4 h-4 mr-2" /> New GRN
              </Button>
            </DialogTrigger>
            {open && <GRNDialog pos={receivablePos} products={products} onDone={() => { setOpen(false); load(); }} />}
          </Dialog>
        }
      />
      <div className="p-4 sm:p-8">
        <div className="border border-[#E5E7EB] overflow-x-auto">
          <table className="w-full text-sm">
            <thead className="bg-[#F9FAFB] border-b border-[#E5E7EB]">
              <tr>{["GRN #", "Date", "PO #", "Vendor", "Warehouse", "Items", "Remarks", ""].map((h, i) => (
                <th key={i} className="text-left label-eyebrow !text-[10px] px-4 py-3">{h}</th>
              ))}</tr>
            </thead>
            <tbody>
              {grns.map((g) => (
                <tr key={g.id} className="border-b border-[#F3F4F6] hover:bg-[#F9FAFB]" data-testid={`pv-grn-row-${g.id}`}>
                  <td className="px-4 py-3 text-xs font-mono font-bold">{g.grn_number}</td>
                  <td className="px-4 py-3 text-xs">{g.receipt_date}</td>
                  <td className="px-4 py-3 text-xs font-mono">{g.po_number}</td>
                  <td className="px-4 py-3 text-xs font-semibold">{vendorName[g.vendor_id] || "—"}</td>
                  <td className="px-4 py-3 text-xs">{g.warehouse}</td>
                  <td className="px-4 py-3 text-xs">
                    {(g.items || []).map((it, idx) => (
                      <div key={idx}>{productName[it.product_id] || "—"}: {it.received_qty} <span className="text-[#9CA3AF]">({it.condition})</span></div>
                    ))}
                  </td>
                  <td className="px-4 py-3 text-xs text-[#4B5563] max-w-xs truncate" title={g.remarks}>{g.remarks || "—"}</td>
                  <td className="px-4 py-3 text-right">
                    <button onClick={() => del(g)} className="text-[#9CA3AF] hover:text-[#DC2626]" data-testid={`pv-grn-del-${g.id}`}><Trash2 className="w-4 h-4" /></button>
                  </td>
                </tr>
              ))}
              {grns.length === 0 && <tr><td colSpan={8} className="text-center text-sm text-[#9CA3AF] py-12">No goods receipts yet.</td></tr>}
            </tbody>
          </table>
        </div>
      </div>
    </div>
  );
}

function GRNDialog({ pos, products, onDone }) {
  const today = new Date().toISOString().slice(0, 10);
  const [poId, setPoId] = useState(pos[0]?.id || "");
  const [receiptDate, setReceiptDate] = useState(today);
  const [warehouse, setWarehouse] = useState("Main Warehouse");
  const [remarks, setRemarks] = useState("");
  const [lines, setLines] = useState({});
  const selected = pos.find((p) => p.id === poId);
  const productName = useMemo(() => Object.fromEntries(products.map((p) => [p.id, p.name])), [products]);

  // Reset lines whenever a new PO is picked — pre-fill with the outstanding qty.
  useEffect(() => {
    if (!selected) { setLines({}); return; }
    const next = {};
    for (const it of (selected.items || [])) {
      const pending = Math.max(0, Number(it.quantity || 0) - Number(it.received_qty || 0));
      next[it.line_id] = { received_qty: pending, condition: "Good", remarks: "" };
    }
    setLines(next);
  }, [selected]);

  const setLineField = (lid, k, v) => setLines({ ...lines, [lid]: { ...lines[lid], [k]: v } });

  const submit = async () => {
    if (!selected) { toast.error("Pick a PO"); return; }
    const items = (selected.items || [])
      .map((it) => {
        const l = lines[it.line_id];
        const qty = Number(l?.received_qty || 0);
        if (qty <= 0) return null;
        return { line_id: it.line_id, received_qty: qty, condition: l.condition || "Good", remarks: l.remarks || null };
      })
      .filter(Boolean);
    if (items.length === 0) { toast.error("Enter received quantity on at least one line"); return; }
    try {
      await api.post("/purchasing/grn", {
        po_id: selected.id, receipt_date: receiptDate, warehouse, remarks: remarks || null, items,
      });
      toast.success("Goods receipt recorded");
      onDone();
    } catch (e) { toast.error(e.response?.data?.detail || "Save failed"); }
  };

  return (
    <DialogContent className="rounded-none border-[#0a0a0a] max-w-3xl max-h-[92vh] overflow-y-auto">
      <DialogHeader><DialogTitle className="font-black text-2xl tracking-tight">New Goods Receipt</DialogTitle></DialogHeader>
      <div className="space-y-3 pt-2">
        <div className="grid grid-cols-2 gap-2">
          <div><Label className="label-eyebrow">Purchase Order *</Label>
            <Select value={poId} onValueChange={setPoId}>
              <SelectTrigger className="rounded-none mt-2 h-10" data-testid="pv-grn-po"><SelectValue placeholder="Pick a PO" /></SelectTrigger>
              <SelectContent>
                {pos.map((p) => <SelectItem key={p.id} value={p.id}>{p.po_number} · {p.status}</SelectItem>)}
                {pos.length === 0 && <SelectItem value="_none" disabled>No receivable POs</SelectItem>}
              </SelectContent>
            </Select>
          </div>
          <div><Label className="label-eyebrow">Receipt Date</Label><Input type="date" value={receiptDate} onChange={(e) => setReceiptDate(e.target.value)} className="rounded-none mt-2 h-10" data-testid="pv-grn-date" /></div>
        </div>
        <div><Label className="label-eyebrow">Warehouse</Label><Input value={warehouse} onChange={(e) => setWarehouse(e.target.value)} className="rounded-none mt-2 h-10" data-testid="pv-grn-warehouse" /></div>

        {selected && (
          <div className="border border-[#E5E7EB]">
            <div className="bg-[#F9FAFB] px-4 py-2 border-b border-[#E5E7EB] label-eyebrow">Line items</div>
            <div className="divide-y divide-[#F3F4F6]">
              {(selected.items || []).map((it, i) => {
                const l = lines[it.line_id] || {};
                const outstanding = Math.max(0, Number(it.quantity || 0) - Number(it.received_qty || 0));
                return (
                  <div key={it.line_id} className="p-3 grid grid-cols-12 gap-2 items-end" data-testid={`pv-grn-line-${i}`}>
                    <div className="col-span-4">
                      <div className="text-xs font-semibold">{productName[it.product_id] || "—"}</div>
                      <div className="text-[10px] text-[#9CA3AF]">
                        Ordered {Number(it.quantity)}, Already received {Number(it.received_qty || 0)}, Pending {outstanding}
                      </div>
                    </div>
                    <div className="col-span-3"><Label className="label-eyebrow !text-[9px]">Received Qty</Label><Input type="number" step="0.01" value={l.received_qty ?? 0} onChange={(e) => setLineField(it.line_id, "received_qty", e.target.value)} className="rounded-none h-9 text-xs" data-testid={`pv-grn-qty-${i}`} /></div>
                    <div className="col-span-3"><Label className="label-eyebrow !text-[9px]">Condition</Label>
                      <Select value={l.condition || "Good"} onValueChange={(v) => setLineField(it.line_id, "condition", v)}>
                        <SelectTrigger className="rounded-none h-9 text-xs" data-testid={`pv-grn-condition-${i}`}><SelectValue /></SelectTrigger>
                        <SelectContent>{CONDITIONS.map((c) => <SelectItem key={c} value={c}>{c}</SelectItem>)}</SelectContent>
                      </Select>
                    </div>
                    <div className="col-span-2"><Label className="label-eyebrow !text-[9px]">Line Remarks</Label><Input value={l.remarks || ""} onChange={(e) => setLineField(it.line_id, "remarks", e.target.value)} className="rounded-none h-9 text-xs" /></div>
                  </div>
                );
              })}
            </div>
            <div className="bg-[#FAFAFA] px-4 py-2 border-t border-[#E5E7EB] text-[10px] text-[#4B5563]">
              Only <b>Good</b> quantities count toward PO fulfilment. Damaged/Rejected are recorded but keep the line outstanding.
            </div>
          </div>
        )}

        <div><Label className="label-eyebrow">Remarks</Label><Textarea value={remarks} onChange={(e) => setRemarks(e.target.value)} rows={2} className="rounded-none mt-2" /></div>
        <Button onClick={submit} className="w-full rounded-none bg-[#002FA7] hover:bg-[#002480] h-11 uppercase tracking-widest text-xs font-bold" data-testid="pv-grn-submit">Record GRN</Button>
      </div>
    </DialogContent>
  );
}
